"use client";

import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { FLOW_COLOR } from "./flowColors";
import { MODE_ICON_PATH, type FlowModeKey } from "./ModeIcon";
type Point = {
  x: number;
  y: number;
};
export type FlowConnectorLane = {
  mode: FlowModeKey | null;
  loadCount: number;
  progress: number;
  isBehind: boolean;
};
export type FlowConnector = {
  id: string;
  from: Point;
  to: Point;
  weight: number;
  isMuted?: boolean;
  lane?: FlowConnectorLane;
};
const LINE = "rgb(var(--ink) / 0.13)";
const MUTED_LINE = "rgb(var(--ink) / 0.07)";
const TRAVELLED = "rgb(var(--ink) / 0.4)";
const MUTED_TRAVELLED = "rgb(var(--ink) / 0.22)";
const ICON = "rgb(var(--ink) / 0.5)";
const MUTED_ICON = "rgb(var(--ink) / 0.3)";
const PILL = "rgb(var(--offwhite))";
const COUNT = "rgb(var(--ink) / 0.6)";
const PILL_HEIGHT = 24;
const ICON_SCALE = 1.15;
const DIGIT_WIDTH = 7;
const SHINE = "rgb(var(--surface) / 0.9)";
const DRIFT_SHARE = 0.025;
const EASE_SPLINES = "0.42 0 0.58 1;0.42 0 0.58 1";
const controlPointsOf = ({
  from,
  to
}: FlowConnector): Point[] => {
  const midX = (from.x + to.x) / 2;
  return [from, {
    x: midX,
    y: from.y
  }, {
    x: midX,
    y: to.y
  }, to];
};
const lerp = (a: Point, b: Point, t: number): Point => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t
});
const curveOf = ([p0, p1, p2, p3]: Point[]) => `M${p0.x} ${p0.y} C${p1.x} ${p1.y} ${p2.x} ${p2.y} ${p3.x} ${p3.y}`;
const splitAt = (points: Point[], t: number) => {
  const [p0, p1, p2, p3] = points;
  const a = lerp(p0, p1, t);
  const b = lerp(p1, p2, t);
  const c = lerp(p2, p3, t);
  const d = lerp(a, b, t);
  const e = lerp(b, c, t);
  const end = lerp(d, e, t);
  return {
    travelled: [p0, a, d, end],
    end
  };
};
const ALL_DESCENDANTS: GetAnimationsOptions = {
  subtree: true // boolean-naming-exempt: external contract — DOM GetAnimationsOptions
};
export function FlowConnectors({
  connectors,
  isPaused = false,
  className
}: {
  connectors: FlowConnector[];
  isPaused?: boolean;
  className?: string;
}) {
  const isReduced = usePrefersReducedMotion();
  const drawnIds = useRef(new Set<string>());
  const svgRef = useRef<SVGSVGElement>(null);
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const sync = () => {
      const shouldPause = isPaused || document.hidden;
      if (shouldPause) svg.pauseAnimations();else svg.unpauseAnimations();
      svg.getAnimations(ALL_DESCENDANTS).forEach(animation => {
        if (shouldPause) animation.pause();else if (animation.playState === "paused") animation.play();
      });
    };
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => document.removeEventListener("visibilitychange", sync);
  }, [isPaused]);
  const drawIn = ({
    element,
    key,
    delay
  }: {
    element: SVGPathElement | null;
    key: string;
    delay: number;
  }) => {
    if (!element || isReduced || drawnIds.current.has(key)) return;
    drawnIds.current.add(key);
    const animation = element.animate([{
      strokeDashoffset: 1
    }, {
      strokeDashoffset: 0
    }], {
      duration: 900,
      delay,
      easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      fill: "backwards"
    });
    if (isPaused || document.hidden) animation.pause();
  };
  return <svg ref={svgRef} aria-hidden="true" className={cn("pointer-events-none absolute inset-0 h-full w-full overflow-visible", className)} data-sentry-element="svg" data-sentry-component="FlowConnectors" data-sentry-source-file="FlowConnectors.tsx">
            {connectors.map((connector, index) => {
      const points = controlPointsOf(connector);
      const lane = connector.lane;
      const hasLane = lane !== undefined && lane.loadCount > 0;
      const split = hasLane ? splitAt(points, lane.progress) : null;
      const label = hasLane ? String(lane.loadCount) : "";
      const pillWidth = PILL_HEIGHT + label.length * DIGIT_WIDTH + 4;
      const iconColor = hasLane && lane.isBehind ? FLOW_COLOR.behind : connector.isMuted ? MUTED_ICON : ICON;
      const delay = 250 + index * 40;
      const drift = split ? splitAt(points, Math.min(0.95, (lane?.progress ?? 0) + DRIFT_SHARE)).end : null;
      const driftBy = split && drift ? `${(drift.x - split.end.x).toFixed(1)} ${(drift.y - split.end.y).toFixed(1)}` : "0 0";
      const isMoving = !isReduced && hasLane;
      return <g key={connector.id}>
                        <path d={curveOf(points)} fill="none" strokeLinecap="round" strokeWidth={connector.weight} pathLength={1} strokeDasharray="1" strokeDashoffset={0} style={{
          stroke: connector.isMuted ? MUTED_LINE : LINE
        }} ref={element => drawIn({
          element,
          key: connector.id,
          delay
        })} />
                        {hasLane && split && <>
                                <path d={curveOf(split.travelled)} fill="none" strokeLinecap="round" strokeWidth={1.5} pathLength={1} strokeDasharray="1" strokeDashoffset={0} style={{
            stroke: lane.isBehind ? FLOW_COLOR.behind : connector.isMuted ? MUTED_TRAVELLED : TRAVELLED
          }} ref={element => drawIn({
            element,
            key: `${connector.id}:travelled`,
            delay: delay + 500
          })} />
                                {isMoving && <path d={curveOf(split.travelled)} fill="none" strokeLinecap="round" strokeWidth={1.5} pathLength={1} strokeDasharray="0.14 1" strokeDashoffset={0.14} style={{
            stroke: SHINE
          }}>
                                        <animate attributeName="stroke-dashoffset" values="0.14;-1;-1" keyTimes="0;0.55;1" dur="5s" begin={`${1.6 + index % 7 * 0.6}s`} repeatCount="indefinite" />
                                    </path>}
                                <g>
                                    {isMoving && <animateTransform attributeName="transform" type="translate" values={`0 0;${driftBy};0 0`} keyTimes="0;0.5;1" calcMode="spline" keySplines={EASE_SPLINES} dur={`${7 + index % 4}s`} begin={`${-(index % 5) * 1.3}s`} repeatCount="indefinite" />}
                                    <rect x={split.end.x - PILL_HEIGHT / 2} y={split.end.y - PILL_HEIGHT / 2} width={pillWidth} height={PILL_HEIGHT} rx={PILL_HEIGHT / 2} style={{
              fill: PILL
            }} />
                                    {lane.mode ? <path d={MODE_ICON_PATH[lane.mode]} fillRule="evenodd" transform={`translate(${split.end.x - 7 * ICON_SCALE} ${split.end.y - 7 * ICON_SCALE}) scale(${ICON_SCALE})`} style={{
              fill: iconColor
            }} /> : <circle cx={split.end.x} cy={split.end.y} r={3.5} style={{
              fill: iconColor
            }} />}
                                    <text x={split.end.x + PILL_HEIGHT / 2} y={split.end.y + 4} style={{
              fill: COUNT,
              fontSize: 11.5,
              fontVariantNumeric: "tabular-nums"
            }}>
                                        {label}
                                    </text>
                                </g>
                            </>}
                    </g>;
    })}
        </svg>;
}